import React, { useEffect, useState } from "react";

const API = "https://canada-billing.ameontech.com";

const styles = {
  wrap: { display: "flex", flexDirection: "column", gap: 20 },
  cardsRow: { display: "flex", gap: 16, flexWrap: "wrap" },
  card: {
    flex: "1 1 200px",
    background: "#fff",
    border: "1px solid #f0f0f0",
    borderRadius: 14,
    padding: "18px 20px",
  },
  cardLabel: { fontSize: 12, color: "#888", fontWeight: 600, marginBottom: 8 },
  cardValue: { fontSize: 24, fontWeight: 700, color: "#1a1a2e", fontFamily: "'Playfair Display', serif" },
  cardDelta: { fontSize: 12, marginTop: 6 },
  up: { color: "#52c97e" },
  down: { color: "#f97b6b" },
  section: {
    background: "#fff",
    border: "1px solid #f0f0f0",
    borderRadius: 14,
    padding: 20,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 14,
    fontFamily: "'Playfair Display', serif",
    color: "#1a1a2e",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    color: "#888",
    fontWeight: 600,
    fontSize: 11,
    textTransform: "uppercase",
    borderBottom: "1px solid #f0f0f0",
  },
  td: { padding: "10px 10px", borderBottom: "1px solid #f5f5f5", color: "#1a1a2e" },
  badge: { padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 },
  paid: { background: "rgba(82,201,126,.12)", color: "#52c97e" },
  pending: { background: "rgba(79,142,247,.12)", color: "#4f8ef7" },
  overdue: { background: "rgba(249,123,107,.12)", color: "#f97b6b" },
  draft: { background: "rgba(136,136,136,.12)", color: "#888" },
  empty: { textAlign: "center", padding: "30px 0", color: "#aaa", fontSize: 13 },
  loading: { textAlign: "center", padding: "60px 0", color: "#aaa", fontSize: 13 },
};

const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusStyle = (s) => {
  if (s === "Paid") return { ...styles.badge, ...styles.paid };
  if (s === "Unpaid") return { ...styles.badge, ...styles.pending };
  if (s === "Draft") return { ...styles.badge, ...styles.draft };
  return { ...styles.badge, ...styles.overdue };
};

export default function Overview() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [clientStats, setClientStats] = useState(null);
  const [overdueCount, setOverdueCount] = useState(0);
  const [recentInvoices, setRecentInvoices] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Not logged in");
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${API}/api/invoices/stats/summary`, { headers }).then((r) => r.json()),
      fetch(`${API}/api/clients/stats`, { headers }).then((r) => r.json()),
      fetch(`${API}/api/invoices/recent?limit=5`, { headers }).then((r) => r.json()),
      fetch(`${API}/api/invoices?status=Unpaid`, { headers }).then((r) => r.json()),
    ])
      .then(([invSummary, cliStats, recent, unpaid]) => {
        setInvoiceStats(invSummary.stats || null);
        setClientStats(cliStats.stats || null);
        setRecentInvoices(recent.invoices || []);

        const today = new Date().toISOString().split("T")[0];
        const overdue = (unpaid.invoices || []).filter(
          (inv) => inv.due_date && inv.due_date.split("T")[0] < today
        );
        setOverdueCount(overdue.length);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={styles.loading}>Loading dashboard…</div>;
  if (error) return <div style={styles.loading}>Error: {error}</div>;

  const stats = [
    {
      label: "Total Revenue",
      value: fmtMoney(invoiceStats?.total_paid),
      sub: `${invoiceStats?.paid || 0} paid invoices`,
      up: true,
    },
    {
      label: "Outstanding",
      value: fmtMoney(invoiceStats?.total_outstanding),
      sub: `${invoiceStats?.unpaid || 0} unpaid invoices`,
      up: false,
    },
    {
      label: "Active Clients",
      value: clientStats?.active ?? 0,
      sub: `${clientStats?.total ?? 0} total clients`,
      up: true,
    },
    {
      label: "Overdue Invoices",
      value: overdueCount,
      sub: "past due date",
      up: overdueCount === 0,
    },
  ];

  return (
    <div style={styles.wrap}>
      <div style={styles.cardsRow}>
        {stats.map((s, i) => (
          <div key={i} style={styles.card}>
            <div style={styles.cardLabel}>{s.label}</div>
            <div style={styles.cardValue}>{s.value}</div>
            <div style={{ ...styles.cardDelta, ...(s.up ? styles.up : styles.down) }}>
              {s.sub}
            </div>
          </div>
        ))}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Recent Invoices</div>
        {recentInvoices.length === 0 ? (
          <div style={styles.empty}>No invoices yet.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Invoice</th>
                <th style={styles.th}>Client</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentInvoices.map((inv) => (
                <tr key={inv.id}>
                  <td style={styles.td}>
                    {inv.invoice_prefix}-{inv.invoice_number}
                  </td>
                  <td style={styles.td}>{inv.buyer_business_name || "—"}</td>
                  <td style={styles.td}>{fmtMoney(inv.total_amount)}</td>
                  <td style={styles.td}>
                    <span style={statusStyle(inv.status)}>{inv.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}